/**
 * Animation Utility Functions
 *
 * Helper functions for managing animations throughout the KimRose Center website
 */

/**
 * Get animation delay for staggered animations
 *
 * @param index - Index of the element in a list
 * @param baseDelay - Base delay in milliseconds (default: 100ms)
 * @returns CSS delay value as a string
 *
 * @example
 * getAnimationDelay(0, 100) // '0ms'
 * getAnimationDelay(2, 100) // '200ms'
 */
export function getAnimationDelay(index: number, baseDelay: number = 100): string {
  return `${index * baseDelay}ms`;
}

/**
 * Get animation class based on direction and visibility
 *
 * @param direction - Animation direction ('up', 'down', 'left', 'right', 'scale')
 * @param isVisible - Whether the element is visible
 * @returns Tailwind animation classes
 *
 * @example
 * getAnimationClass('up', true) // 'animate-fade-in-up'
 * getAnimationClass('left', false) // 'opacity-0'
 */
export function getAnimationClass(direction: string, isVisible: boolean): string {
  if (!isVisible) {
    return 'opacity-0';
  }

  const animationMap: Record<string, string> = {
    up: 'animate-fade-in-up',
    down: 'animate-fade-in-down',
    left: 'animate-fade-in-left',
    right: 'animate-fade-in-right',
    scale: 'animate-scale-in',
  };

  return animationMap[direction] || 'animate-fade-in-up';
}

/**
 * Throttle function to limit function execution rate
 * Useful for scroll event handlers to improve performance
 *
 * @param func - Function to throttle
 * @param limit - Time limit in milliseconds
 * @returns Throttled function
 *
 * @example
 * const handleScroll = throttle(() => {
 *   console.log('Scrolling...');
 * }, 100);
 */
export function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return function (this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Smooth scroll to a specific element or section
 * Accounts for sticky header offset
 *
 * @param targetId - ID of the target element
 * @param offset - Additional offset in pixels (default: 80px for sticky header)
 *
 * @example
 * scrollToSection('contact-section', 80)
 */
export function scrollToSection(targetId: string, offset: number = 80): void {
  const element = document.getElementById(targetId);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
}
